import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  ArrowLeftRight,
  ArrowRight,
  Plus,
  Home,
  Car,
  ShoppingBag,
  Utensils,
  Zap,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrency } from '@/contexts';
import { useTranslation } from '@/hooks/useTranslation';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
  to_account?: Database['public']['Tables']['accounts']['Row'] | null;
};

type TransactionTypeFilter = 'all' | 'income' | 'expense' | 'transfer';

interface RecentTransactionsProps {
  transactions: Transaction[];
  isLoading?: boolean;
}

// Category icon mapping
const getCategoryIcon = (categoryName: string | undefined | null): React.ElementType => {
  const iconMap: Record<string, React.ElementType> = {
    housing: Home,
    logement: Home,
    transport: Car,
    shopping: ShoppingBag,
    food: Utensils,
    alimentation: Utensils,
    utilities: Zap,
    énergie: Zap,
  };
  const name = categoryName?.toLowerCase() || '';
  for (const [key, icon] of Object.entries(iconMap)) {
    if (name.includes(key)) return icon;
  }
  return CreditCard;
};

export function RecentTransactions({ transactions, isLoading = false }: RecentTransactionsProps) {
  const navigate = useNavigate();
  const { formatAmountInCurrency, isSecretMode } = useCurrency();
  const { t, language } = useTranslation();
  const [typeFilter, setTypeFilter] = useState<TransactionTypeFilter>('all');

  // Filter by type
  const filteredTransactions = useMemo(() => {
    let filtered = transactions.filter((t) => !t.is_recurring);
    if (typeFilter !== 'all') {
      filtered = filtered.filter((t) => t.type === typeFilter);
    }
    return filtered.slice(0, 8);
  }, [transactions, typeFilter]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    if (date.toDateString() === now.toDateString()) {
      return t('dashboard.transactions.today');
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return t('dashboard.transactions.yesterday');
    }
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
        </div>
        <div className="flex gap-2 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg flex-1" />
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-xl" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-1" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em] mb-1">
            {t('dashboard.transactions.title')}
          </p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {filteredTransactions.length} {t('dashboard.transactions.title').toLowerCase()}
          </p>
        </div>
        <button
          onClick={() => navigate('/transactions')}
          className="text-[#16c47f] hover:text-[#0ea572] text-sm font-medium flex items-center gap-1 group"
        >
          {t('dashboard.transactions.all')}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Type Filter Tabs */}
      <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-gray-700/60 rounded-xl p-1 mb-5">
        {(
          [
            { key: 'all', label: t('dashboard.filter.all' as any) || 'Tous' },
            { key: 'income', label: t('dashboard.filter.income' as any) || 'Revenus' },
            { key: 'expense', label: t('dashboard.filter.expense' as any) || 'Dépenses' },
            { key: 'transfer', label: t('dashboard.filter.transfer' as any) || 'Transferts' },
          ] as const
        ).map((item) => (
          <button
            key={item.key}
            onClick={() => setTypeFilter(item.key)}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
              typeFilter === item.key
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Transactions List */}
      {filteredTransactions.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
            {t('dashboard.transactions.empty')}
          </p>
          <Button
            onClick={() => navigate('/transactions')}
            className="bg-[#16c47f] hover:bg-[#12b070] text-white font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard.transactions.add')}
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {filteredTransactions.map((transaction, index) => {
            const isTransfer = transaction.type === 'transfer';
            const isExpense = transaction.type === 'expense';
            const Icon = isTransfer
              ? ArrowLeftRight
              : isExpense
                ? getCategoryIcon(transaction.category?.name)
                : TrendingUp;

            return (
              <div
                key={transaction.id}
                onClick={() => navigate('/transactions')}
                className="flex items-center justify-between px-3 py-2.5 -mx-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors cursor-pointer group"
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isTransfer
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-400'
                        : isExpense
                          ? 'bg-[#78909C]/10 dark:bg-[#78909C]/20 text-[#78909C]'
                          : 'bg-green-50 dark:bg-green-900/20 text-[#16c47f]'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate">
                      {transaction.description ||
                        (isTransfer
                          ? t('transactions.filter.transfer')
                          : transaction.category?.name) ||
                        'Transaction'}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <p className="text-[11px] text-gray-400 dark:text-gray-500">
                        {formatDate(transaction.date)}
                      </p>
                      {isTransfer && transaction.account && transaction.to_account ? (
                        <span className="text-[11px] text-gray-400 truncate max-w-[120px]">
                          · {transaction.account.name} → {transaction.to_account.name}
                        </span>
                      ) : (
                        transaction.category && (
                          <span className="text-[11px] text-gray-400 truncate max-w-[100px]">
                            · {transaction.category.name}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
                <span
                  className={`text-sm font-bold tabular-nums flex-shrink-0 ml-2 ${
                    isTransfer
                      ? 'text-blue-600 dark:text-blue-400'
                      : isExpense
                        ? 'text-[#78909C]'
                        : 'text-[#16c47f]'
                  }`}
                >
                  {isTransfer ? '' : isExpense ? '-' : '+'}
                  {isSecretMode
                    ? '•••'
                    : formatAmountInCurrency(
                        transaction.amount,
                        transaction.account?.currency || 'EUR'
                      )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* View All Button */}
      {filteredTransactions.length >= 5 && (
        <button
          onClick={() => navigate('/transactions')}
          className="w-full mt-4 py-2.5 text-xs font-medium text-[#16c47f] hover:text-[#12b070] flex items-center justify-center gap-1.5 bg-[#16c47f]/5 hover:bg-[#16c47f]/10 rounded-xl transition-colors"
        >
          {t('dashboard.transactions.viewAllBtn')}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
